import { useTranslation } from "react-i18next";

import { VacancyDataField } from "./VacancyDataField";

import Icons from "@/components/icons";
import { isNullOrEmpty } from "@/lib/utils/stringUtils";
import { VacancyType } from "@/types/vacancy/VacancyType";
import { useValueFormatter } from "@/utils/useValueFormatter";

export const VacancyDataSection: React.FC<{ vacancy: VacancyType }> = ({
  vacancy,
}) => {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const hoursLabel = t("common.hours");

  // Salaries arrive as free-text strings, so a non-numeric one ("negotiable") is real content, not
  // missing data — show it verbatim rather than formatting NaN into an N/A.
  const formatSalary = (value: string | null) => {
    if (isNullOrEmpty(value)) return "";
    const amount = Number(value);
    return Number.isFinite(amount) ? format(amount, "currency") : value!.trim();
  };

  const salaryFrom = formatSalary(vacancy.salaryFrom);
  const salaryTo =
    isNullOrEmpty(vacancy.salaryTo) || vacancy.salaryFrom === vacancy.salaryTo
      ? ""
      : ` - ${formatSalary(vacancy.salaryTo)}`;
  const salaryPer = isNullOrEmpty(vacancy.salaryPer)
    ? ""
    : ` per ${vacancy.salaryPer}`;
  const salaryValue = `${salaryFrom}${salaryTo}${salaryPer}`;

  // Format contract hours: show range if both min/max exist, otherwise fall back to contractHours
  const formatContractHours = (): string => {
    if (
      !isNullOrEmpty(vacancy.hoursPerWeekMin) &&
      !isNullOrEmpty(vacancy.hoursPerWeekMax)
    ) {
      const min = vacancy.hoursPerWeekMin;
      const max = vacancy.hoursPerWeekMax;
      if (min === max) {
        return `${min} ${hoursLabel}`;
      }
      return `${min} - ${max} ${hoursLabel}`;
    }
    // Fallback to contractHours for backward compatibility
    if (!isNullOrEmpty(vacancy.contractHours)) {
      return `${vacancy.contractHours} ${hoursLabel}`;
    }
    return "";
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <VacancyDataField
        icon={<Icons.SellIcon fontSize={24} />}
        value={vacancy.field}
        label="Field"
      />
      <VacancyDataField
        icon={<Icons.ScheduleIcon fontSize={24} />}
        value={formatContractHours()}
        label="Contract hours"
      />
      <VacancyDataField
        icon={<Icons.CurrencyIcon fontSize={24} />}
        value={salaryValue}
        label="Salary"
      />
      <VacancyDataField
        icon={<Icons.LocationOnIcon fontSize={24} />}
        value={vacancy.vacancyLoc?.city}
        label="Location"
      />
      <VacancyDataField
        icon={<Icons.WorkIcon fontSize={24} />}
        value={vacancy.experience}
        label="Experience"
      />
      <VacancyDataField
        icon={<Icons.SchoolIcon fontSize={24} />}
        value={vacancy.education}
        label="Education"
      />
      <VacancyDataField
        icon={<Icons.ContractIocn fontSize={24} />}
        value={vacancy.contractType}
        label="Contract type"
      />
      <VacancyDataField
        icon={<Icons.ClientsIcon fontSize={24} />}
        value={vacancy.company}
        label="Company"
      />
      <VacancyDataField
        icon={<Icons.StoreIcon fontSize={24} />}
        value={vacancy.department}
        label="Department"
      />
    </div>
  );
};
